"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/components/lib/utils";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";

const SnaplandModalRoot = DialogPrimitive.Root;
const SnaplandModalTrigger = DialogPrimitive.Trigger;
const SnaplandModalPortal = DialogPrimitive.Portal;
const SnaplandModalClose = DialogPrimitive.Close;

const SnaplandModalOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { snaplandId?: string }
>(({ className, snaplandId, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-50 bg-black/50 backdrop-blur-sm", className)}
    data-snapland-id={snaplandId || "snapland-modal-overlay"}
    {...props}
  />
));
SnaplandModalOverlay.displayName = "SnaplandModalOverlay";

const SnaplandModalContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    showCloseButton?: boolean;
    snaplandId?: string;
  }
>(({ className, children, showCloseButton = false, snaplandId, ...props }, ref) => {
  const { t } = useI18n();
  const baseId = snaplandId || "snapland-modal";
  return (
    <SnaplandModalPortal>
      <SnaplandModalOverlay snaplandId={`${baseId}-overlay`} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-4 md:top-1/2 z-50 w-full max-w-lg -translate-x-1/2 translate-y-0 md:-translate-y-1/2 border bg-[hsl(var(--color-card))] p-0 text-[hsl(var(--color-card-foreground))] shadow-lg border-[hsl(var(--color-border))] rounded-lg flex flex-col max-h-[calc(100vh-2rem)] min-h-0 overflow-x-visible overflow-y-hidden",
          className
        )}
        data-snapland-id={baseId}
        {...props}
      >
        {showCloseButton && (
          <SnaplandModalClose asChild>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 h-8 w-8 p-0 rounded-none bg-transparent text-[hsl(var(--sidebar-background))] hover:bg-[hsl(var(--sidebar-background))] hover:text-[hsl(var(--sidebar-background))]"
              snaplandId={`${baseId}-close-button`}
            >
              <X className="h-4 w-4" data-snapland-id={`${baseId}-close-icon`} />
              <span className="sr-only" data-snapland-id={`${baseId}-close-sr-only`}>{t('common.close')}</span>
            </Button>
          </SnaplandModalClose>
        )}
        {children}
      </DialogPrimitive.Content>
    </SnaplandModalPortal>
  );
});
SnaplandModalContent.displayName = "SnaplandModalContent";

function SnaplandModalHeader({ 
  className, 
  children,
  showCloseButton = true,
  onClose,
  snaplandId,
  ...props 
}: React.HTMLAttributes<HTMLDivElement> & {
  showCloseButton?: boolean;
  onClose?: () => void;
  snaplandId?: string;
}) {
  const { t } = useI18n();
  const baseId = snaplandId || "snapland-modal-header";
  return (
    <div 
      className={cn(
        "flex-shrink-0 flex flex-row items-center justify-between px-2 py-2 border-b border-[hsl(var(--color-border))] rounded-t-lg bg-[hsl(var(--color-card))]",
        className
      )}
      data-snapland-id={baseId}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SnaplandModalClose asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8 p-0 rounded-full bg-[hsl(var(--sidebar-background))]/10 hover:bg-[hsl(var(--sidebar-background))]/20 text-[hsl(var(--sidebar-background))] transition-all duration-200 ms-4 flex-shrink-0"
            snaplandId={`${baseId}-close-button`}
          >
            <X className="h-4 w-4" data-snapland-id={`${baseId}-close-icon`} />
            <span className="sr-only" data-snapland-id={`${baseId}-close-sr-only`}>{t('common.close')}</span>
          </Button>
        </SnaplandModalClose>
      )}
    </div>
  );
}

function SnaplandModalTitle({
  className,
  icon,
  snaplandId,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  icon?: React.ReactNode;
  snaplandId?: string;
}) {
  const baseId = snaplandId || "snapland-modal-title";
  if (icon) {
    return (
      <div className="flex items-center gap-2" data-has-icon="true" data-snapland-id={`${baseId}-container`}>
        <div className="p-1.5 rounded-full bg-[hsl(var(--sidebar-background))]/10 flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4" data-snapland-id={`${baseId}-icon-wrapper`}>
          {icon}
        </div>
        <DialogPrimitive.Title
          className={cn("font-semibold text-[hsl(var(--sidebar-background))] flex-1", className)}
          data-snapland-id={baseId}
          {...props}
        >
          {props.children}
        </DialogPrimitive.Title>
      </div>
    );
  }
  
  return (
    <DialogPrimitive.Title
      className={cn("font-semibold text-[hsl(var(--sidebar-background))]", className)}
      data-snapland-id={baseId}
      {...props}
    >
      {props.children}
    </DialogPrimitive.Title>
  );
}

function SnaplandModalDescription({
  className,
  snaplandId,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { snaplandId?: string }) {
  const baseId = snaplandId || "snapland-modal-description";
  return (
    <DialogPrimitive.Description
      data-snapland-description
      className={cn("text-[hsl(var(--sidebar-background))]/80 p-0", className)}
      data-snapland-id={baseId}
      {...props}
    />
  );
}

function SnaplandModalBody({ className, snaplandId, ...props }: React.HTMLAttributes<HTMLDivElement> & { snaplandId?: string }) {
  const baseId = snaplandId || "snapland-modal-body";
  return <div className={cn("p-4 flex-1 min-h-0 overflow-y-auto", className)} data-snapland-id={baseId} {...props} />;
}

function SnaplandModalFooter({ className, snaplandId, ...props }: React.HTMLAttributes<HTMLDivElement> & { snaplandId?: string }) {
  const baseId = snaplandId || "snapland-modal-footer";
  return (
    <div
      className={cn(
        "mt-auto flex-shrink-0 flex justify-end gap-2 px-2 py-2 border-t border-[hsl(var(--color-border))] bg-[hsl(var(--color-card))] rounded-b-lg",
        className
      )}
      data-snapland-id={baseId}
      {...props}
    />
  );
}

export {
  SnaplandModalRoot as SnaplandModal,
  SnaplandModalTrigger,
  SnaplandModalContent,
  SnaplandModalHeader,
  SnaplandModalTitle,
  SnaplandModalDescription,
  SnaplandModalBody,
  SnaplandModalFooter,
  SnaplandModalClose,
};

